import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import PropTypes from 'prop-types';
import TabContainer from './Navigation/TabContainer';
import GrowthReports from './GrowthReports';
import OperationalReports from './OperationalReports';
import RecoveryGoalSettingReport from './Reports/RecoveryGoalSettingReport';
import MaintenanceAlert from './Reports/MaintenanceAlert';
import ReportsQueue from './ReportsQueue';
import Settings from 'resources/settings';
import tabDetails from './Navigation/tabDetails';
import { updateHeaderHelpLink } from './utils';
import LaunchDarklyWrapper from '../utils/LaunchDarkly/LaunchDarklyWrapper';
import styles from './MainContainer.module.css';
import AppHeader from './AppHeader';
import Banner from './Banner';

const propTypes = {
  bannerClose: PropTypes.func.isRequired,
  bannerShown: PropTypes.bool.isRequired,
  menus: PropTypes.object.isRequired,
  userName: PropTypes.string.isRequired,
  partnerBid: PropTypes.string.isRequired,
  location: PropTypes.shape().isRequired,
  availableTabs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  reportDetails: PropTypes.objectOf(
    PropTypes.shape({
      urlEndPoint: PropTypes.string.isRequired,
      isEnabled: PropTypes.bool.isRequired,
      disabledMessage: PropTypes.string.isRequired,
    })
  ).isRequired,
  allGrowthReportDetailsForUser: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  filteredGrowthReportDetails: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  growthReportAccordionDetails: PropTypes.instanceOf(Map).isRequired,
  operationalReportDetails: PropTypes.arrayOf(PropTypes.object).isRequired,
  operationalReportAccordionDetails: PropTypes.instanceOf(Map).isRequired,
  defaultSelectedTabEndpoint: PropTypes.string.isRequired,
  educatorBid: PropTypes.string.isRequired,
  onAccordionStateChange: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onSortSelection: PropTypes.func.isRequired,
  sortSelection: PropTypes.shape({
    isExpanded: PropTypes.bool.isRequired,
    value: PropTypes.string.isRequired,
    previousValue: PropTypes.string.isRequired,
  }).isRequired,
  sortExpandStateFn: PropTypes.func.isRequired,
  sessionToken: PropTypes.shape().isRequired,
};

/**
 * This component handles the allowable routes, rendering the appropriate component for the route.
 * The following will happen when a user clicks a navigation tab:
 *
 * 1 - The Link element (provided by react router) changes the window location
 * 2 - The <BrowserRouter> element within <MainContainer> (one level up from here) renders its <Route>
 *     (which catches all route changes), passing the URL location to this component as a PROP
 * 3 - The <Switch> statement below matches the location to the path of the <Route>, and renders the
 *     child component for the matching route
 *
 * Transitions:
 *   - <TransitionGroup> tracks its child components via their keys.
 *   - When the <Switch> component renders a new component, the <TransitionGroup> delays the removal
 *     of the route which is leaving by a designated time
 *   - <CSSTransition> handles the changing of the classes on exiting/entering components to provide
 *     the transition effects
 *
 * See docs here:
 *  - https://reacttraining.com/react-router/
 *  - https://reactcommunity.org/react-transition-group/transition-group
 */
export function Routes({
  bannerClose,
  bannerShown,
  menus,
  userName,
  partnerBid,
  location,
  availableTabs,
  reportDetails,
  allGrowthReportDetailsForUser,
  filteredGrowthReportDetails,
  growthReportAccordionDetails,
  operationalReportDetails,
  operationalReportAccordionDetails,
  defaultSelectedTabEndpoint,
  educatorBid,
  onAccordionStateChange,
  onResetFilters,
  onSortSelection,
  sortSelection,
  sortExpandStateFn,
  sessionToken,
}) {
  const startingIndex = location.pathname.lastIndexOf('/') + 1;
  const endpoint = location.pathname.substring(startingIndex);
  const mainClasses = `${styles['main-container']} ${styles['nwea__grid__container']} ${styles['nwea__grid__fluid']} ${styles[endpoint]}`;
  const { pathname } = location;
  const calculateTransitionTime = () => {
    return styles.transitionTime * 2 * 1000;
  };
  return (
    <React.Fragment>
      <AppHeader userName={userName} menu={updateHeaderHelpLink(pathname, menus)} title={''} />
      <TabContainer availableTabs={availableTabs} locationPathName={location.pathname} />

      {/* NOTE: BANNER is currently turned off - If needed, replace static text to updated text and set bannerShown={bannerShown} */}
      <Banner closeButtonFunc={bannerClose} bannerShown={false} />

      <TransitionGroup component={null} enter={true} exit={true}>
        <CSSTransition key={location.key} classNames={styles} timeout={calculateTransitionTime()}>
          <Switch location={location}>
            <Route
              exact={true}
              path={`${Settings.URL_BASE_PATH}${tabDetails.growthReports.urlEndPoint}`}
              render={() => (
                <main data-testid="main-container" className={mainClasses} key={tabDetails.growthReports.urlEndPoint}>
                  <LaunchDarklyWrapper>
                    <GrowthReports
                      allReportDetails={allGrowthReportDetailsForUser}
                      filteredGrowthReportDetails={filteredGrowthReportDetails}
                      accordionDetails={growthReportAccordionDetails}
                      onAccordionStateChange={onAccordionStateChange}
                      onResetFilters={onResetFilters}
                      sortSelection={sortSelection}
                      onSortSelection={onSortSelection}
                      sortExpandStateFn={sortExpandStateFn}
                      sessionToken={sessionToken}
                    />
                  </LaunchDarklyWrapper>
                </main>
              )}
            />
            <Route
              exact={true}
              path={`${Settings.URL_BASE_PATH}${tabDetails.operationalReports.urlEndPoint}`}
              render={() => (
                <main className={mainClasses} key={tabDetails.operationalReports.urlEndPoint}>
                  <OperationalReports
                    reportDetails={operationalReportDetails}
                    accordionDetails={operationalReportAccordionDetails}
                    onAccordionStateChange={onAccordionStateChange}
                    sessionToken={sessionToken}
                  />
                </main>
              )}
            />
            <Route
              exact={true}
              path={`${Settings.URL_BASE_PATH}${tabDetails.reportsQueue.urlEndPoint}`}
              render={() => (
                <main className={mainClasses} key={tabDetails.reportsQueue.urlEndPoint}>
                  <ReportsQueue
                    educatorBid={educatorBid}
                    partnerBid={partnerBid}
                    sessionToken={sessionToken}
                    transitionTime={calculateTransitionTime()}
                  />
                </main>
              )}
            />
            <Route
              exact={true}
              path={`${Settings.URL_BASE_PATH}${reportDetails.recoveryGoalSetting.urlEndPoint}`}
              render={() => {
                return reportDetails.recoveryGoalSetting.isEnabled ? (
                  <RecoveryGoalSettingReport
                    userName={userName}
                    educatorBid={educatorBid}
                    partnerBid={partnerBid}
                    sessionToken={sessionToken}
                  />
                ) : (
                  <>
                    {/* The recovery report is now fully disabled and so direct link will now redirect
                                         /* to the growthReports page of RLP. The following was used when recovery report was
                                         /* enabled and under maintenance in case we ever re-enable recovery report.
                                         /* <MaintenanceAlert message={reportDetails.recoveryGoalSetting.disabledMessage} />
                                         */}
                    <Redirect to={`${Settings.URL_BASE_PATH}${tabDetails.growthReports.urlEndPoint}`} />
                  </>
                );
              }}
            />
          </Switch>
        </CSSTransition>
      </TransitionGroup>
    </React.Fragment>
  );
}

Routes.propTypes = propTypes;

export default Routes;
